import { useMemo, useState, type ReactNode } from 'react';
import {
  Box,
  Button as MantineButton,
  Group,
  Loader,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { type DatesRangeValue } from '@mantine/dates';
import { FinanceDateRangeInput } from '@repo/date-range-picker';
import { LineChart } from '@mantine/charts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useNavigate } from 'react-router-dom';
import {
  IoBasketOutline,
  IoCashOutline,
  IoCubeOutline,
  IoTrendingUpOutline,
} from 'react-icons/io5';
import {
  SELLER_ME_QUERY_KEY,
  sellerOnboardingApi,
} from '@modules/seller-onboarding/api';
import { sellerSalesApi, type SellerDashboardDay } from '@modules/seller-sales/api/seller-sales.api';
import { Alert, Button, PillGroup } from '@shared/ui';
import { useIsMobile } from '@shared/hooks';
import { formatPrice } from '@shared/utils';

dayjs.extend(utc);

function localCalendarYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

type PeriodPreset = 'today' | 'yesterday' | 'week' | 'month' | 'custom';

function utcRangeForPreset(preset: PeriodPreset): { dateFrom: string; dateTo: string } {
  const end = dayjs.utc();
  if (preset === 'today') {
    const d = end.format('YYYY-MM-DD');
    return { dateFrom: d, dateTo: d };
  }
  if (preset === 'yesterday') {
    const y = end.subtract(1, 'day').format('YYYY-MM-DD');
    return { dateFrom: y, dateTo: y };
  }
  if (preset === 'week') {
    return {
      dateFrom: end.subtract(6, 'day').format('YYYY-MM-DD'),
      dateTo: end.format('YYYY-MM-DD'),
    };
  }
  if (preset === 'month') {
    return {
      dateFrom: end.startOf('month').format('YYYY-MM-DD'),
      dateTo: end.format('YYYY-MM-DD'),
    };
  }
  const d = end.format('YYYY-MM-DD');
  return { dateFrom: d, dateTo: d };
}

function parseYmd(s: string): dayjs.Dayjs | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s.trim());
  if (!m) return null;
  return dayjs.utc(`${m[1]}-${m[2]}-${m[3]}`, 'YYYY-MM-DD', true);
}

/** Подпись на оси X графиков: день.месяц, без года */
function formatChartAxisDate(value: string | number): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value).trim());
  if (!m) return String(value);
  return `${m[3]}.${m[2]}`;
}

const chartXAxisProps = { tickFormatter: formatChartAxisDate };

/** Ось Y выручки: тысячи рублей, например 3,5т */
function formatRevenueAxisThousands(v: number): string {
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  if (n === 0) return '0т';
  const k = n / 1000;
  const roundedTenth = Math.round(k * 10) / 10;
  if (Math.abs(roundedTenth - Math.round(roundedTenth)) < 1e-6) {
    return `${Math.round(roundedTenth)}т`;
  }
  return `${roundedTenth.toFixed(1).replace('.', ',')}т`;
}

const revenueChartYAxisProps = { tickFormatter: formatRevenueAxisThousands };

/** Ось Y «заказов»: только целые числа */
const ordersChartYAxisProps = {
  allowDecimals: false,
  tickFormatter: (v: number) => String(Math.round(Number(v))),
};

function fillDailySeries(
  from: string,
  to: string,
  daily: SellerDashboardDay[],
): { date: string; revenue: number; ordersCount: number }[] {
  const start = parseYmd(from);
  const end = parseYmd(to);
  if (!start || !end || end.isBefore(start)) {
    return daily.map((d) => ({
      date: d.date,
      revenue: d.revenue,
      ordersCount: d.ordersCount,
    }));
  }
  const byDay = new Map(daily.map((d) => [d.date, d]));
  const out: { date: string; revenue: number; ordersCount: number }[] = [];
  for (let d = start; !d.isAfter(end); d = d.add(1, 'day')) {
    const key = d.format('YYYY-MM-DD');
    const row = byDay.get(key);
    out.push({
      date: key,
      revenue: row?.revenue ?? 0,
      ordersCount: row?.ordersCount ?? 0,
    });
  }
  return out;
}

const PRESET_LABELS: { id: PeriodPreset; label: string }[] = [
  { id: 'today', label: 'Сегодня' },
  { id: 'yesterday', label: 'Вчера' },
  { id: 'week', label: 'Неделя' },
  { id: 'month', label: 'Месяц' },
  { id: 'custom', label: 'Свой период' },
];

export default function SellerDashboardSection(props: {
  hideApprovedOnboardingBanner?: boolean;
}) {
  const hideApprovedOnboardingBanner = props.hideApprovedOnboardingBanner ?? false;
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const chartHeight = isMobile ? 200 : 260;
  const queryClient = useQueryClient();
  const dismissBanner = useMutation({
    mutationFn: () => sellerOnboardingApi.dismissApprovedOnboardingBanner(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SELLER_ME_QUERY_KEY });
    },
  });

  const [preset, setPreset] = useState<PeriodPreset>('week');
  const [customRange, setCustomRange] = useState<DatesRangeValue>([null, null]);

  const { dateFrom, dateTo } = useMemo(() => {
    if (preset === 'custom') {
      const [a, b] = customRange;
      if (!a || !b) {
        return utcRangeForPreset('week');
      }
      const from = typeof a === 'string' ? a : localCalendarYmd(a);
      const to = typeof b === 'string' ? b : localCalendarYmd(b);
      if (from <= to) return { dateFrom: from, dateTo: to };
      return { dateFrom: to, dateTo: from };
    }
    return utcRangeForPreset(preset);
  }, [preset, customRange]);

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ['seller-sales', 'me', 'dashboard', dateFrom, dateTo],
    queryFn: () => sellerSalesApi.getMeDashboard({ dateFrom, dateTo }),
  });

  const errMsg =
    error && typeof error === 'object' && 'response' in error
      ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
      : undefined;

  const chartData = useMemo(() => {
    if (!data) return [];
    return fillDailySeries(dateFrom, dateTo, data.daily);
  }, [data, dateFrom, dateTo]);

  return (
    <div className="seller-dashboard">
      <Box className="seller-dashboard__sticky" pos="sticky" top={0} pb="md" mb="md">
        <div className="seller-dashboard__head">
          <div>
            <Title order={2} className="seller-dashboard__title">
              Главная
            </Title>
            <Text className="seller-dashboard__subtitle">
              Обзор продаж и ключевых показателей магазина
            </Text>
          </div>
        </div>

        {!hideApprovedOnboardingBanner ? (
          <div className="seller-dashboard-approved-banner">
            <div className="seller-dashboard-approved-banner__body">
              <Text className="seller-dashboard-approved-banner__title">
                Ваша заявка одобрена. Что дальше?
              </Text>
              <Text className="seller-dashboard-approved-banner__text">
                Сначала точки самовывоза, затем товары на витрину — краткая памятка по разделам
                кабинета.
              </Text>
              <Button
                type="button"
                variant="primary"
                className="seller-dashboard-approved-banner__cta"
                onClick={() => navigate('/onboarding')}
              >
                Первые шаги
              </Button>
            </div>
            <MantineButton
              type="button"
              variant="subtle"
              size="compact-sm"
              className="seller-dashboard-approved-banner__dismiss"
              loading={dismissBanner.isPending}
              onClick={() => dismissBanner.mutate()}
            >
              Больше не показывать
            </MantineButton>
          </div>
        ) : null}

        <div className="seller-dashboard__period">
          <Text className="seller-dashboard__period-label">Период</Text>
          <PillGroup
            ariaLabel="Период"
            options={PRESET_LABELS.map((p) => ({ value: p.id, label: p.label }))}
            value={preset}
            onChange={setPreset}
          />
          {preset === 'custom' ? (
            <div className="seller-dashboard__period-dates">
              <FinanceDateRangeInput
                value={customRange}
                onChange={setCustomRange}
                valueFormat="DD.MM.YYYY"
                placeholder="Выберите даты"
                size="sm"
                radius="md"
              />
            </div>
          ) : null}
        </div>
      </Box>

      <Stack gap="lg" className="seller-dashboard__content">
        {isLoading && !data ? (
          <Loader />
        ) : isError ? (
          <Alert tone="error" title="Не удалось загрузить статистику">
            {typeof errMsg === 'string' ? errMsg : 'Попробуйте обновить страницу или войти снова.'}
          </Alert>
        ) : data ? (
          <>
            <SimpleGrid
              cols={{ base: 2, sm: 2, lg: 4 }}
              spacing={{ base: 8, sm: 'md' }}
              className="seller-dashboard__metrics"
            >
              <MetricCard
                title="Выручка"
                hint="Доставленные заказы"
                value={formatPrice(data.grossRevenue)}
                loading={isFetching}
                tone="accent"
                icon={<IoCashOutline />}
              />
              <MetricCard
                title="Заказы"
                hint="С вашими товарами"
                value={String(data.ordersCount)}
                loading={isFetching}
                tone="blue"
                icon={<IoBasketOutline />}
              />
              <MetricCard
                title="Ср. чек"
                hint="Выручка / заказы"
                value={data.ordersCount > 0 ? formatPrice(data.averageOrderValue) : '—'}
                loading={isFetching}
                tone="orange"
                icon={<IoTrendingUpOutline />}
              />
              <MetricCard
                title="Единиц"
                hint="По позициям"
                value={String(data.itemsSold)}
                loading={isFetching}
                tone="green"
                icon={<IoCubeOutline />}
              />
            </SimpleGrid>

            <Group align="stretch" grow wrap="wrap" gap="md" className="seller-dashboard__charts">
              <section className="seller-dashboard-card seller-dashboard-card--chart">
                <div className="seller-dashboard-card__head">
                  <Title order={4} className="seller-dashboard-card__title">
                    Выручка по дням
                  </Title>
                </div>
                {chartData.length === 0 ? (
                  <Text c="dimmed" size="sm">
                    Нет данных за период
                  </Text>
                ) : (
                  <LineChart
                    h={chartHeight}
                    data={chartData}
                    dataKey="date"
                    series={[{ name: 'revenue', color: 'pink.6', label: 'Выручка, тыс. ₽' }]}
                    curveType="monotone"
                    withTooltip
                    withLegend={false}
                    xAxisProps={chartXAxisProps}
                    yAxisProps={revenueChartYAxisProps}
                    gridAxis="xy"
                  />
                )}
              </section>
              <section className="seller-dashboard-card seller-dashboard-card--chart">
                <div className="seller-dashboard-card__head">
                  <Title order={4} className="seller-dashboard-card__title">
                    Заказы по дням
                  </Title>
                </div>
                {chartData.length === 0 ? (
                  <Text c="dimmed" size="sm">
                    Нет данных за период
                  </Text>
                ) : (
                  <LineChart
                    h={chartHeight}
                    data={chartData}
                    dataKey="date"
                    series={[{ name: 'ordersCount', color: 'cyan.6', label: 'Заказы' }]}
                    curveType="monotone"
                    withTooltip
                    withLegend={false}
                    xAxisProps={chartXAxisProps}
                    yAxisProps={ordersChartYAxisProps}
                    gridAxis="xy"
                  />
                )}
              </section>
            </Group>

            <section className="seller-dashboard-card">
              <div className="seller-dashboard-card__head">
                <Title order={4} className="seller-dashboard-card__title">
                  Топ‑5 товаров
                </Title>
              </div>
              {data.topProducts.length === 0 ? (
                <Text c="dimmed" size="sm" ta="center" py="xl">
                  Нет продаж за период
                </Text>
              ) : (
                <>
                  <div className="seller-dashboard-table-wrap">
                    <Table
                      className="seller-dashboard-table"
                      highlightOnHover
                      withRowBorders={false}
                      horizontalSpacing="md"
                      verticalSpacing="sm"
                    >
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>Товар</Table.Th>
                          <Table.Th>Продано, шт.</Table.Th>
                          <Table.Th>Выручка</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {data.topProducts.map((r) => (
                          <Table.Tr key={r.productId}>
                            <Table.Td>{r.name}</Table.Td>
                            <Table.Td>{r.quantitySold}</Table.Td>
                            <Table.Td>{formatPrice(r.revenue)}</Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  </div>
                  <div className="seller-dashboard-top-mobile" aria-label="Топ товаров">
                    {data.topProducts.map((r, index) => (
                      <div key={r.productId} className="seller-dashboard-top-mobile__row">
                        <span className="seller-dashboard-top-mobile__rank">{index + 1}</span>
                        <div className="seller-dashboard-top-mobile__main">
                          <span className="seller-dashboard-top-mobile__name">{r.name}</span>
                          <span className="seller-dashboard-top-mobile__meta">
                            {r.quantitySold} шт. · {formatPrice(r.revenue)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </section>
          </>
        ) : null}
      </Stack>
    </div>
  );
}

function MetricCard(props: {
  title: string;
  hint: string;
  value: string;
  valueColor?: string;
  loading?: boolean;
  tone?: 'accent' | 'blue' | 'orange' | 'green';
  icon?: ReactNode;
}) {
  const tone = props.tone ?? 'accent';
  return (
    <div className={`seller-dashboard-metric seller-dashboard-metric--${tone}`}>
      <div className="seller-dashboard-metric__top">
        <Text className="seller-dashboard-metric__title">{props.title}</Text>
        <span className="seller-dashboard-metric__dot" aria-hidden>
          {props.icon}
        </span>
      </div>
      <Text className="seller-dashboard-metric__value" c={props.valueColor}>
        {props.loading ? '…' : props.value}
      </Text>
      <Text className="seller-dashboard-metric__hint">{props.hint}</Text>
    </div>
  );
}
